import { stringify } from 'qs';
import { apiRoot } from 'common';

type ResponseHandler = (res: Response) => Promise<any>;

type FetcherResponse<T> = {
  url: string;
  status: number;
  data: T;
  errors: any;
  error: any;
};

export type LegacyIIQApiOptions = {
  getAuth0AccessToken?: () => Promise<string | undefined>;
  onUnauthorized?: () => Promise<void>;
};

export class LegacyIIQApi {
  _getAuth0AccessToken?: () => Promise<string | undefined>;
  _onUnauthorized: NonNullable<LegacyIIQApiOptions['onUnauthorized']>;

  constructor({
    getAuth0AccessToken,
    onUnauthorized,
  }: LegacyIIQApiOptions = {}) {
    this._getAuth0AccessToken = getAuth0AccessToken;
    this._onUnauthorized = onUnauthorized ?? (() => Promise.resolve());
  }

  private async getHeaders(additionalHeaders: Record<string, any> = {}) {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-client-id': 'portal-webapp',
      ...additionalHeaders,
    };

    if (this._getAuth0AccessToken) {
      try {
        const auth0AccessToken = await this._getAuth0AccessToken();
        // If we don't have a token, we don't need to set the header as this is probably an anonymous request
        if (auth0AccessToken) {
          headers.authorization = `Bearer ${auth0AccessToken}`;
        }
      } catch (error) {
        console.error(
          '[LegacyIIQApi] Error getting Auth0 access token:',
          error
        );
      }
    }
    return headers;
  }

  private static getHandlerKey = (url: string, method?: string) => {
    const routePatterns = [
      /.+media\/(.+)\/(url|download|signedUrl)/,
      /.+policies\/(.+)\/(application)/,
      /.+login$/,
    ];

    const matches = routePatterns.reduce((acc: string[], pattern) => {
      const match = url.match(pattern);

      if (match) {
        acc.push(match[1]);
      }
      return acc;
    }, []);

    url = matches.reduce((acc, match) => acc.replace(match, ''), url);

    const apiKey = url
      .replace(new RegExp(`^(.*?)${apiRoot}/`), '')
      .replace(/^[a-z]/g, (m) => m.toUpperCase())
      .replace(/(\/[a-z])/g, (m) => m.slice(1).toUpperCase())
      .replace(/(\/)/g, (m) => m.slice(1).toUpperCase());

    return `${method?.toLowerCase() ?? 'get'}${apiKey}`;
  };

  private static handleRawErrors: ResponseHandler = async (res: Response) => {
    console.debug('Handling raw errors for response:', res);
    const textBody = await res.clone().text();
    console.debug('Response text body:', textBody);

    const { error, status, ...data } = await res.json().catch(() => {
      console.debug('Failed to parse JSON, attempting to parse as HTML');
      const parser = new DOMParser();
      const doc = parser.parseFromString(textBody, 'text/html');
      const parsedError = doc.body.innerText.trim();
      console.debug('Parsed HTML error:', parsedError);
      return {
        status: res.status,
        data: null,
        error: parsedError,
      };
    });
  };

  private static responseHandlers: Record<string, ResponseHandler> = {
    getMediaDownload: async (res: Response) => {
      let filename = res.headers.get('x-filename');
      if (!filename) {
        filename = res.headers.get('content-disposition');
        if (filename) {
          filename = filename.split('filename=')[1];
        } else {
          const match = /.+media\/(.*)\/download/.exec(res.url);
          filename = match?.[1] ?? 'download';
        }
      }

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      if (filename) {
        link.download = decodeURIComponent(filename);
      }
      link.click();
    },
    getMediaUrl: async (res: Response) => {
      const url = await res.text();
      window.open(url);
    },
    getPoliciesApplicationUrl(res: Response) {
      return this.getMediaUrl(res);
    },
    postConfirm: LegacyIIQApi.handleRawErrors,
    postLogin: LegacyIIQApi.handleRawErrors,
  };

  private async fetcher<T = unknown>(
    requestUrl: URL | string,
    {
      handler,
      ...optionsParam
    }: RequestInit & { handler?: ResponseHandler | null } = {}
  ): Promise<FetcherResponse<T>> {
    const headers = await this.getHeaders(optionsParam.headers);

    const fetcherOptions: RequestInit = {
      method: 'GET',
      credentials: 'include',
      ...optionsParam,
      headers,
    };
    console.debug(`Fetcher options for url "${requestUrl}":`, fetcherOptions);

    const res = await fetch(requestUrl, fetcherOptions);
    console.debug('Fetch response:', res);

    const { url, status } = res;

    if (status === 401) {
      try {
        await this._onUnauthorized();
      } catch (err) {
        console.error(
          `[LegacyIIQApi]Error calling _onUnauthorized for ${url}:`,
          err
        );
      }

      // Return early on 401s
      return {
        url,
        status,
        data: null as T,
        errors: ['Unauthorized'],
        error: 'Unauthorized',
      };
    }

    const handlerKey = LegacyIIQApi.getHandlerKey(url, fetcherOptions.method);

    if (LegacyIIQApi.responseHandlers[handlerKey]) {
      console.debug('Using response handler for key:', handlerKey);
      return LegacyIIQApi.responseHandlers[handlerKey](res);
    }

    if (handler) {
      console.debug('Using custom handler');
      return handler(res);
    }

    if (status === 0) {
      console.info('User network error');
    }

    let jsonErrors: unknown;
    let jsonError: unknown;
    let restData: unknown = null;

    try {
      const { errors, error, ...jsonData } = await res.json();
      jsonErrors = errors;
      jsonError = error;
      restData = jsonData;
      console.debug('Parsed JSON response:', jsonData);
    } catch (e) {
      console.error('Error parsing JSON response:', e);
    }

    return {
      url,
      status,
      data: (restData &&
      typeof restData === 'object' &&
      Object.keys(restData as any).length
        ? restData
        : null) as T,
      errors: jsonErrors || (jsonError && [jsonError]),
      error: jsonError || (jsonErrors && (jsonErrors as any)[0]),
    };
  }

  static urlString(path: string) {
    const hostname = window.location.origin;
    return `${hostname}${apiRoot}${path}`;
  }

  assign(path: string, params: Record<string, any>) {
    let urlString = LegacyIIQApi.urlString(path);
    if (params) {
      urlString += `?${stringify(params)}`;
    }
    window.location.assign(urlString);
  }

  get<T = unknown>(
    path: string,
    params?: Record<string, any>,
    headers: Record<string, any> = {}
  ) {
    let urlString = LegacyIIQApi.urlString(path);
    if (params && !!Object.keys(params).length) {
      urlString += `?${stringify(params)}`;
    }
    return this.fetcher<T>(urlString, {
      method: 'GET',
      headers,
    });
  }

  post<T = unknown>(
    path: string,
    params?: Record<string, any>,
    headers: Record<string, any> = {},
    handler: ResponseHandler | null = null
  ) {
    const urlString = LegacyIIQApi.urlString(path);
    return this.fetcher<T>(urlString, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
      handler,
    });
  }

  upload(path: string, file: string | Blob) {
    const urlString = LegacyIIQApi.urlString(path);
    const formData = new FormData();
    formData.append('files', file);

    return this.fetcher(urlString, {
      method: 'POST',
      body: formData,
    });
  }

  download(
    path: string,
    params?: Record<string, any>,
    headers: Record<string, any> = {}
  ) {
    let urlString = LegacyIIQApi.urlString(path);
    if (params) {
      urlString += `?${stringify(params)}`;
    }
    return this.fetcher(urlString, {
      method: 'GET',
      headers,
      handler: LegacyIIQApi.responseHandlers.getMediaDownload,
    });
  }

  patch<T = unknown>(
    path: string,
    params?: Record<string, any>,
    headers: Record<string, any> = {}
  ) {
    const urlString = LegacyIIQApi.urlString(path);
    return this.fetcher<T>(urlString, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(params),
    });
  }

  put<T = unknown>(
    path: string,
    params?: Record<string, any>,
    headers: Record<string, any> = {}
  ) {
    const urlString = LegacyIIQApi.urlString(path);
    return this.fetcher<T>(urlString, {
      method: 'PUT',
      headers,
      body: JSON.stringify(params),
    });
  }

  delete<T = unknown>(
    path: string,
    params?: Record<string, any>,
    headers: Record<string, any> = {}
  ) {
    const urlString = LegacyIIQApi.urlString(path);
    return this.fetcher<T>(urlString, {
      method: 'DELETE',
      headers,
      body: JSON.stringify(params),
    });
  }
}

export const api = new LegacyIIQApi();
